import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useToast } from '../../hooks/useToast';
import {
  checkActiveSyncJobs,
  createPartnerCopyOfDefaultAutomation,
  duplicateDefaultTemplates,
  getAutomationStatuses,
  getAutomationTemplate,
  getAutomationTemplates,
  getDefaultAutomationMetadata,
  syncAutomationTemplates,
  updateAutomationTemplate,
} from './engage-automations-tab/template-api';
import type {
  AutomationStatusesResponse,
  UpdateAutomationTemplateParams,
} from './engage-automations-tab/templates.types';

/** Sub-jobs use @reach/shared-types/jobs JobStatusEnum — include all terminal rows so spinners clear. */
const TERMINAL_JOB_STATUSES = ['COMPLETED', 'FAILED', 'CANCELLED'];

export const templateKeys = {
  all: ['automationTemplates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  detail: (id: string) => [...templateKeys.all, 'detail', id] as const,
  defaultMetadata: () => [...templateKeys.all, 'defaultMetadata'] as const,
  /** GET /sync/status/:templateId — per-template active sync + sub-jobs */
  templateSyncStatus: (templateId: string) =>
    [...templateKeys.all, 'templateSyncStatus', templateId] as const,
  automationStatuses: (templateId: string) =>
    [...templateKeys.all, 'automationStatuses', templateId] as const,
};

/**
 * Hook to fetch all automation templates for the authenticated partner
 */
export function useAutomationTemplates() {
  return useQuery({
    queryKey: templateKeys.lists(),
    queryFn: () => getAutomationTemplates(),
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
  });
}

/**
 * Hook to update an automation template
 */
export function useUpdateAutomationTemplate() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateAutomationTemplateParams }) =>
      updateAutomationTemplate(id, params),
    onSuccess: (_, variables) => {
      // Invalidate templates list
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists(),
      });
      // Invalidate specific template
      queryClient.invalidateQueries({
        queryKey: templateKeys.detail(variables.id),
      });
      addToast({
        title: 'Saved',
        description: 'Template changes saved successfully',
        variant: 'default',
      });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save changes.',
        variant: 'destructive',
      });
    },
  });
}

export function useDuplicateDefaultTemplates() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: () => duplicateDefaultTemplates(),
    onSuccess: result => {
      // Invalidate templates list to refetch
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists(),
      });

      // Show success or info toast based on result
      if (result.createdCount > 0) {
        addToast({
          title: 'Success!',
          description: `Added ${result.createdCount} default template(s)`,
          variant: 'default',
        });
      } else {
        addToast({
          title: 'No templates added',
          description:
            'No default templates were found to add. Make sure the default-templates.ts file is populated.',
          variant: 'default',
        });
      }
    },
    onError: (error: Error) => {
      console.error('Error adding default templates:', error);
      addToast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to add default templates. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export type SyncAutomationTemplatesParams = {
  templateId?: string;
  businessIds?: string[];
  /** When true, skip the "Sync Started" toast (e.g. when sync is triggered as part of save). */
  silent?: boolean;
};

export function useSyncAutomationTemplates() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: SyncAutomationTemplatesParams) =>
      syncAutomationTemplates(
        params == null ? undefined : { templateId: params.templateId, businessIds: params.businessIds },
      ),
    onSuccess: (_, variables) => {
      if (variables?.templateId) {
        queryClient.invalidateQueries({
          queryKey: templateKeys.templateSyncStatus(variables.templateId),
        });
        // Job row may not exist on the first poll after POST /sync — nudge a follow-up fetch.
        const tid = variables.templateId;
        window.setTimeout(() => {
          if (tid) {
            queryClient.invalidateQueries({
              queryKey: templateKeys.templateSyncStatus(tid),
            });
          }
        }, 2000);
        queryClient.invalidateQueries({
          queryKey: templateKeys.automationStatuses(variables.templateId),
        });
      }
      if (variables?.silent) return;
      addToast({
        title: 'Sync Started',
        description:
          variables?.templateId && variables?.businessIds?.length
            ? `Sync has been queued for ${variables.businessIds.length} tenant(s). Refresh to see updated statuses.`
            : 'Automation template sync job has been queued',
        variant: 'default',
      });
    },
    onError: (error: Error) => {
      console.error('Error syncing automation templates:', error);
      addToast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to sync automation templates. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export function useDefaultAutomationMetadata() {
  return useQuery({
    queryKey: templateKeys.defaultMetadata(),
    queryFn: () => getDefaultAutomationMetadata(),
    staleTime: 300000, // Consider data fresh for 5 minutes (metadata doesn't change often)
    gcTime: 600000, // Keep in cache for 10 minutes
  });
}

export function useCreatePartnerCopyOfDefaultAutomation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateName: string) => createPartnerCopyOfDefaultAutomation(templateName),
    onSuccess: () => {
      // Invalidate templates list to refetch
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists(),
      });
    },
  });
}

export function useAutomationTemplate(id: string | undefined) {
  return useQuery({
    queryKey: templateKeys.detail(id || ''),
    queryFn: () => getAutomationTemplate(id!),
    enabled: !!id,
    staleTime: 30000,
    gcTime: 300000,
  });
}

export function useAutomationStatuses(
  templateId: string | undefined,
  options?: {
    /** While template sync is in progress, poll so tenant badges catch up as each sub-job completes. */
    refetchInterval?: number | false;
  },
) {
  return useQuery<AutomationStatusesResponse>({
    queryKey: templateKeys.automationStatuses(templateId || ''),
    queryFn: () => getAutomationStatuses(templateId!),
    enabled: !!templateId,
    staleTime: 15000,
    gcTime: 60000,
    refetchInterval: options?.refetchInterval ?? false,
  });
}

/**
 * Poll GET /sync/status?templateId= on the automation template editor. Spinners and sync UI come
 * only from this response (`hasActiveJobs`, `subJobs`). Refetches every 2s while a parent job is active.
 */
export function useTemplateSyncStatusPolling(templateId: string | undefined) {
  const query = useQuery({
    queryKey: templateKeys.templateSyncStatus(templateId ?? ''),
    queryFn: () => checkActiveSyncJobs(templateId!),
    enabled: !!templateId,
    staleTime: 0,
    refetchInterval: q => {
      if (!templateId) return false;
      return q.state.data?.hasActiveJobs ? 2000 : false;
    },
    refetchOnWindowFocus: true,
  });

  const syncingTenantIds = useMemo(() => {
    const d = query.data;
    if (!d?.hasActiveJobs || !d.subJobs) return [];
    return d.subJobs
      .filter(s => s.businessId && !TERMINAL_JOB_STATUSES.includes(s.status))
      .map(s => s.businessId as string);
  }, [query.data]);

  const isFullyComplete = useMemo(
    () => !!templateId && query.data != null && !query.data.hasActiveJobs,
    [templateId, query.data],
  );

  return {
    ...query,
    syncingTenantIds,
    isFullyComplete,
  };
}
